/**
 * Product Page Interactions
 * Handles quantity steppers, read more, and mobile interactions
 */

document.addEventListener('DOMContentLoaded', function() {
  const lastVariantByScope = new WeakMap();
  
  // ========== QUANTITY STEPPER ==========
  function normalizeQuantity(rawValue) {
    const parsedValue = parseInt(rawValue, 10);
    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      return 1;
    }
    return parsedValue;
  }

  function updateStepperButtonState(stepper, value) {
    if (!stepper) return;
    const decreaseBtn = stepper.querySelector('[data-quantity-action="decrease"]');
    if (decreaseBtn) {
      decreaseBtn.disabled = value <= 1;
    }
  }

  function syncAllQuantities(value, sourceInput = null, options = {}) {
    const { preserveSourceInput = false, scopeRoot = document } = options;
    const root = scopeRoot || document;
    const normalizedValue = normalizeQuantity(value);

    // Hidden/submit quantity fields used by forms.
    root.querySelectorAll('input[name="quantity"]').forEach((input) => {
      input.value = normalizedValue;
    });

    // Visible custom quantity inputs in desktop/mobile steppers.
    root.querySelectorAll('[data-quantity-input]').forEach((input) => {
      const shouldPreserveSourceValue = preserveSourceInput && input === sourceInput;
      if (!shouldPreserveSourceValue) {
        input.value = normalizedValue;
      }
      updateStepperButtonState(input.closest('[data-quantity-stepper]'), normalizedValue);
    });

    const stickyCount = root.querySelector('.sticky-bar__count');
    if (stickyCount) {
      stickyCount.textContent = normalizedValue + ' selected';
    }

    const stickyPriceEl = root.querySelector('#sticky-bar-price');
    if (stickyPriceEl) {
      const basePrice = Number.parseInt(stickyPriceEl.dataset.basePrice, 10) || 0;
      const symbol = stickyPriceEl.dataset.currencySymbol || '£';
      const totalPrice = basePrice * normalizedValue;
      const formattedPrice = new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(totalPrice / 100);

      stickyPriceEl.textContent = `${symbol}${formattedPrice} Ex. VAT`;
    }
  }

  function getScopedProductRoot(node) {
    if (!(node instanceof Element)) return null;
    return node.closest('product-info');
  }

  function getScopeVariantInput(scopeRoot) {
    if (!scopeRoot) return null;
    return scopeRoot.querySelector('input[name="id"]');
  }

  function rememberVariantForScope(scopeRoot, variantId) {
    if (!scopeRoot || !variantId) return;
    lastVariantByScope.set(scopeRoot, String(variantId));
  }

  function resetQuantitiesIfVariantChanged(scopeRoot, variantId) {
    if (!scopeRoot || !variantId) return;

    const normalizedVariantId = String(variantId);
    const previousVariantId = lastVariantByScope.get(scopeRoot);
    if (previousVariantId === normalizedVariantId) return;

    lastVariantByScope.set(scopeRoot, normalizedVariantId);
    syncAllQuantities(1, null, { scopeRoot });
  }

  function initQuantitySteppers(root = document) {
    const quantitySteppers = root.querySelectorAll('[data-quantity-stepper]');
    if (!quantitySteppers.length) return;

    const syncRoots = new Set();

    quantitySteppers.forEach((stepper) => {
      if (stepper.dataset.quantityStepperInitialized === 'true') {
        return;
      }

      const input = stepper.querySelector('[data-quantity-input]');
      if (!input) {
        return;
      }

      const scopeRoot = stepper.closest('product-info') || root;
      syncRoots.add(scopeRoot);

      stepper.dataset.quantityStepperInitialized = 'true';
      input.value = normalizeQuantity(input.value);
      updateStepperButtonState(stepper, normalizeQuantity(input.value));

      stepper.querySelectorAll('[data-quantity-action]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();

          const action = button.dataset.quantityAction;
          const currentValue = normalizeQuantity(input.value);
          const nextValue = action === 'increase'
            ? currentValue + 1
            : Math.max(1, currentValue - 1);

          syncAllQuantities(nextValue, null, { scopeRoot });
        });
      });

      input.addEventListener('input', function() {
        this.value = this.value.replace(/[^0-9]/g, '');

        if (this.value === '') {
          updateStepperButtonState(stepper, 0);
          return;
        }

        syncAllQuantities(this.value, this, {
          preserveSourceInput: true,
          scopeRoot,
        });
      });

      input.addEventListener('blur', function() {
        syncAllQuantities(this.value, this, { scopeRoot });
      });

      input.addEventListener('change', function() {
        syncAllQuantities(this.value, this, { scopeRoot });
      });
    });

    if (!syncRoots.size) {
      syncRoots.add(root);
    }

    syncRoots.forEach((scopeRoot) => {
      const initialInput = scopeRoot.querySelector('[data-quantity-input], input[name="quantity"]');
      syncAllQuantities(initialInput ? initialInput.value : 1, initialInput || null, { scopeRoot });

      const variantInput = getScopeVariantInput(scopeRoot);
      if (variantInput?.value) {
        rememberVariantForScope(scopeRoot, variantInput.value);
      }
    });
  }

  initQuantitySteppers();
  document.addEventListener('product-info:loaded', (event) => {
    const loadedRoot = event.target instanceof HTMLElement ? event.target : document;
    initQuantitySteppers(loadedRoot);
  });

  // Covers custom swatches that manually update hidden variant inputs.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'id') return;

    const scopeRoot = getScopedProductRoot(target);
    if (!scopeRoot) return;

    resetQuantitiesIfVariantChanged(scopeRoot, target.value);
  });
  
  // ========== READ MORE FUNCTIONALITY ==========
  const readMoreLinks = document.querySelectorAll('.read-more-link');
  
  readMoreLinks.forEach(link => {
    link.addEventListener('click', function(e) {
      e.preventDefault();
      const aboutText = this.closest('.about-text');
      const aboutContainer = this.closest('.about-container');
      
      if (aboutText && aboutContainer) {
        aboutText.classList.toggle('about-text--expanded');
        
        if (aboutText.classList.contains('about-text--expanded')) {
          this.textContent = 'Read less';
          // Show full description
          const fullDesc = aboutContainer.dataset.fullDescription;
          if (fullDesc) {
            aboutText.innerHTML = fullDesc + ' <a href="#product-tabs" class="read-more-link">Read less</a>';
          }
        } else {
          this.textContent = 'Read more';
        }
      }
    });
  });

  // ========== MOBILE THUMBNAIL INTERACTION ==========
  function initMobileThumbSlider() {
    const mobileSlider = document.querySelector('[data-mobile-thumb-slider]');
    const mobileThumbTrack = mobileSlider?.querySelector('[data-mobile-thumb-track]') || null;
    const mobilePrevBtn = mobileSlider?.querySelector('[data-mobile-thumb-prev]') || null;
    const mobileNextBtn = mobileSlider?.querySelector('[data-mobile-thumb-next]') || null;

    if (!mobileThumbTrack || !mobilePrevBtn || !mobileNextBtn) return;

    // Remove existing listeners if re-initializing
    const newPrevBtn = mobilePrevBtn.cloneNode(true);
    const newNextBtn = mobileNextBtn.cloneNode(true);
    mobilePrevBtn.parentNode.replaceChild(newPrevBtn, mobilePrevBtn);
    mobileNextBtn.parentNode.replaceChild(newNextBtn, mobileNextBtn);

    function getMobileThumbnails() {
      return Array.from(mobileThumbTrack.querySelectorAll('.mobile-thumbnail'));
    }

    function getActiveThumbIndex(thumbnails) {
      const activeIndex = thumbnails.findIndex((thumb) => thumb.classList.contains('mobile-thumbnail--active'));
      return activeIndex >= 0 ? activeIndex : 0;
    }

    function updateMobileThumbButtons() {
      const thumbnails = getMobileThumbnails();
      if (!thumbnails.length) {
        newPrevBtn.disabled = true;
        newNextBtn.disabled = true;
        return;
      }

      const activeIndex = getActiveThumbIndex(thumbnails);
      newPrevBtn.disabled = activeIndex <= 0;
      newNextBtn.disabled = activeIndex >= thumbnails.length - 1;
    }
    
    newPrevBtn.addEventListener('click', () => {
      const thumbnails = getMobileThumbnails();
      if (!thumbnails.length) return;

      const activeIndex = getActiveThumbIndex(thumbnails);
      const prevThumb = thumbnails[Math.max(activeIndex - 1, 0)];
      if (prevThumb) prevThumb.click();
    });
    
    newNextBtn.addEventListener('click', () => {
      const thumbnails = getMobileThumbnails();
      if (!thumbnails.length) return;

      const activeIndex = getActiveThumbIndex(thumbnails);
      const nextThumb = thumbnails[Math.min(activeIndex + 1, thumbnails.length - 1)];
      if (nextThumb) nextThumb.click();
    });

    let rafId = null;
    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateMobileThumbButtons();
      });
    };

    mobileThumbTrack.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    
    // Initial check (with a small delay to ensure rendering)
    setTimeout(updateMobileThumbButtons, 100);
  }

  // Initialize mobile thumbnails
  initMobileThumbSlider();
  
  // Re-initialize on re-renders
  document.addEventListener('product-info:loaded', initMobileThumbSlider);

  // Use event delegation for mobile thumbnail clicks
  document.addEventListener('click', function(e) {
    const thumbnail = e.target.closest('.mobile-thumbnail');
    if (!thumbnail) return;

    const mediaId = thumbnail.dataset.mediaId;
    const mobileThumbnails = document.querySelectorAll('.mobile-thumbnail');
    const mobileMediaItems = document.querySelectorAll('.mobile-main-image-item');
    const mobileThumbTrack = document.querySelector('[data-mobile-thumb-track]');
    
    // Update thumbnails active state
    mobileThumbnails.forEach(t => t.classList.remove('mobile-thumbnail--active'));
    thumbnail.classList.add('mobile-thumbnail--active');

    // Scroll into view
    thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    
    // Update main images visibility
    mobileMediaItems.forEach(item => {
      if (item.dataset.mediaId === mediaId) {
        item.classList.remove('hidden');
        item.classList.add('active');
        item.setAttribute('aria-hidden', 'false');
      } else {
        item.classList.add('hidden');
        item.classList.remove('active');
        item.setAttribute('aria-hidden', 'true');
      }
    });

    if (mobileThumbTrack) {
      const thumbnails = Array.from(mobileThumbTrack.querySelectorAll('.mobile-thumbnail'));
      const activeIndex = thumbnails.findIndex(t => t.classList.contains('mobile-thumbnail--active'));
      const mobileSlider = thumbnail.closest('[data-mobile-thumb-slider]') || document.querySelector('[data-mobile-thumb-slider]');
      const prevBtn = mobileSlider?.querySelector('[data-mobile-thumb-prev]');
      const nextBtn = mobileSlider?.querySelector('[data-mobile-thumb-next]');

      if (prevBtn && nextBtn && activeIndex >= 0) {
        prevBtn.disabled = activeIndex <= 0;
        nextBtn.disabled = activeIndex >= thumbnails.length - 1;
      }
    }
  });

  // ========== ADD TO BASKET / SOLD OUT LABEL ==========
  function updateAddToBasketButtons(isAvailable) {
    const buttons = document.querySelectorAll('.btn-add-to-cart, .sticky-bar__btn');
    buttons.forEach((button) => {
      const labelEl = button.querySelector('span');
      if (!labelEl) return;

      const addText = button.dataset.addText || 'Add to Basket';
      const soldOutText = button.dataset.soldOutText || 'Out of stock';

      if (isAvailable) {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        labelEl.textContent = addText;
      } else {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
        labelEl.textContent = soldOutText;
      }
    });
  }

  // Initialize from server-rendered state
  (function initAddToBasketState() {
    const desktopButton = document.querySelector('.btn-add-to-cart');
    if (!desktopButton) return;
    const isAvailable =
      !desktopButton.hasAttribute('disabled') &&
      desktopButton.getAttribute('aria-disabled') !== 'true';
    updateAddToBasketButtons(isAvailable);
  })();

  // Keep in sync when variant changes
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const available = Boolean(data?.variant?.available);
      updateAddToBasketButtons(available);

      const sectionId = data?.sectionId;
      const scopedRoot = sectionId
        ? document.querySelector(`product-info[data-section="${sectionId}"]`)
        : document.querySelector('product-info');

      if (scopedRoot && data?.variant?.id) {
        resetQuantitiesIfVariantChanged(scopedRoot, data.variant.id);
      }
    });
  }
  
  // ========== MOBILE TIMER FIX ==========
  // Initialize mobile timer with same logic as desktop
  initializeMobileTimer();
  
});

// ========== MOBILE TIMER INITIALIZATION ==========
function initializeMobileTimer() {
  const mobileTimer = document.getElementById('delivery-countdown-timer-mobile');
  
  if (mobileTimer && typeof DeliveryCountdown !== 'undefined') {
    new DeliveryCountdown(mobileTimer);
  } else if (mobileTimer) {
    // Fallback if DeliveryCountdown class not available
    startSimpleCountdown(mobileTimer);
  }
}

function startSimpleCountdown(element) {
  function updateTimer() {
    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(13, 0, 0, 0); // 1 PM cutoff
    
    if (now >= cutoff) {
      cutoff.setDate(cutoff.getDate() + 1);
    }
    
    const diff = cutoff - now;
    
    if (diff <= 0) {
      element.innerHTML = '<span class="countdown-value">00H</span> : <span class="countdown-value">00M</span> : <span class="countdown-value">00 sec(s)</span>';
      return;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    const h = hours.toString().padStart(2, '0');
    const m = minutes.toString().padStart(2, '0');
    const s = seconds.toString().padStart(2, '0');
    
    element.innerHTML = `<span class="countdown-value">${h}H</span> : <span class="countdown-value">${m}M</span> : <span class="countdown-value">${s} sec(s)</span>`;
  }
  
  updateTimer();
  setInterval(updateTimer, 1000);
}
